import { Head, Link } from '@inertiajs/react';
import { PublicLayout } from '@/components/public/PublicLayout';
import { motion } from '@/components/public/PublicMotion';
import { fadeInUp, staggerContainer } from '@/hooks/use-scroll-animation';
import { Button } from '@/components/ui/button';
import { CheckCircle, Mail, CalendarDays, Handshake, ArrowRight } from 'lucide-react';

const Grazie = () => {
  return (
    <PublicLayout>
      <Head title="Emovia | Grazie">
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      {/* SEZIONE A — Confirmation hero */}
      <section className="py-20 md:py-28 bg-green-lighter">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, type: 'spring', stiffness: 200 }}
            >
              <div className="w-24 h-24 rounded-full bg-green-light border-4 border-green/30 flex items-center justify-center mx-auto mb-8">
                <CheckCircle className="w-12 h-12 text-green-dark" />
              </div>
            </motion.div>

            <h1 className="font-display text-4xl md:text-5xl font-bold text-green-dark mb-4">
              Richiesta ricevuta
            </h1>

            <p className="text-lg text-green-dark/80 leading-relaxed mb-2">
              Ti contatteremo entro 24 ore lavorative.
            </p>

            <p className="text-base text-green-dark/60">
              Controlla la posta — potresti ricevere una conferma automatica.
            </p>
          </div>
        </div>
      </section>

      {/* SEZIONE B — Cosa succede ora */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary text-center mb-12">
              Cosa succede ora
            </h2>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="grid md:grid-cols-3 gap-8"
            >
              {/* Step 1 */}
              <motion.div
                variants={fadeInUp}
                className="bg-card rounded-2xl border border-border p-6 text-center"
              >
                <span className="text-xs font-bold text-primary uppercase tracking-widest mb-2 block">
                  01
                </span>
                <div className="w-14 h-14 rounded-2xl bg-blue-accent flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Ti contattiamo</h3>
                <p className="text-sm text-muted-foreground">
                  Entro 24 ore lavorative ti scriviamo o chiamiamo per capire le tue esigenze.
                </p>
              </motion.div>

              {/* Step 2 */}
              <motion.div
                variants={fadeInUp}
                className="bg-card rounded-2xl border border-border p-6 text-center"
              >
                <span className="text-xs font-bold text-primary uppercase tracking-widest mb-2 block">
                  02
                </span>
                <div className="w-14 h-14 rounded-2xl bg-blue-accent flex items-center justify-center mx-auto mb-4">
                  <CalendarDays className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Demo personalizzata</h3>
                <p className="text-sm text-muted-foreground">
                  Ti mostriamo Emovia nel concreto, adattato al tuo metodo clinico.
                </p>
              </motion.div>

              {/* Step 3 */}
              <motion.div
                variants={fadeInUp}
                className="bg-card rounded-2xl border border-border p-6 text-center"
              >
                <span className="text-xs font-bold text-primary uppercase tracking-widest mb-2 block">
                  03
                </span>
                <div className="w-14 h-14 rounded-2xl bg-green-light flex items-center justify-center mx-auto mb-4">
                  <Handshake className="w-7 h-7 text-green-dark" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Decidi senza pressioni</h3>
                <p className="text-sm text-muted-foreground">
                  Nessun impegno. Valuti se Emovia fa per te e il tuo studio.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SEZIONE C — CTA contestuale */}
      <section className="py-16 md:py-20 bg-blue-lighter/40">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="max-w-2xl mx-auto text-center"
          >
            <p className="text-sm font-medium text-primary uppercase tracking-wider mb-4">
              Nel frattempo
            </p>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary mb-4">
              Scopri come funziona Emovia
            </h2>
            <p className="text-muted-foreground mb-8">
              Capire il flusso ti aiuterà a fare le domande giuste durante la demo.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" asChild className="text-base px-8 h-12">
                <Link href="/come-funziona">
                  Scopri come funziona
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="text-base px-8 h-12">
                <Link href="/">Torna alla home</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Grazie;
